/**
 * utils/leadOrigin.js — TREATX-LEAD-ORIGIN-DISPLAY-01
 *
 * ONE display mapping for "where did this lead come from", mirroring the
 * canonical server authority `services/case_messaging_v3_service.resolve_lead_origin`:
 * `lead_source_category` first, then the `source_type` enum, then the legacy
 * `source` string. Identity masking and commission are decided server-side and
 * are NOT affected by this helper — it only decides what the provider UI shows.
 *
 * An unresolved origin is reported as 'UNKNOWN'. It is never inferred to be
 * TreatX Marketplace, because that would misstate the commission channel.
 */

export const LEAD_ORIGIN = {
  MARKETPLACE: 'TREATX_MARKETPLACE',
  PROVIDER_OWNED: 'PROVIDER_OWNED',
  UNKNOWN: 'UNKNOWN',
};

const CATEGORY_MAP = {
  treatx_sourced: LEAD_ORIGIN.MARKETPLACE,
  provider_sourced: LEAD_ORIGIN.PROVIDER_OWNED,
};

const LEGACY_SOURCE_MAP = {
  marketplace: LEAD_ORIGIN.MARKETPLACE,
  treatx: LEAD_ORIGIN.MARKETPLACE,
  treatx_marketplace: LEAD_ORIGIN.MARKETPLACE,
  platform: LEAD_ORIGIN.MARKETPLACE,
  clinic: LEAD_ORIGIN.PROVIDER_OWNED,
  clinic_sourced: LEAD_ORIGIN.PROVIDER_OWNED,
  provider: LEAD_ORIGIN.PROVIDER_OWNED,
  provider_owned: LEAD_ORIGIN.PROVIDER_OWNED,
  provider_sourced: LEAD_ORIGIN.PROVIDER_OWNED,
  clinic_own: LEAD_ORIGIN.PROVIDER_OWNED,
  own: LEAD_ORIGIN.PROVIDER_OWNED,
};

const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase().replace(/[-\s]/g, '_') : '');

export const resolveLeadOrigin = (lead) => {
  if (!lead) return LEAD_ORIGIN.UNKNOWN;

  const category = CATEGORY_MAP[norm(lead.lead_source_category || lead.leadSourceCategory)];
  if (category) return category;

  const explicit = (lead.source_type || lead.sourceType || '').toString().toUpperCase();
  if (explicit === LEAD_ORIGIN.MARKETPLACE || explicit === LEAD_ORIGIN.PROVIDER_OWNED) {
    return explicit;
  }

  return LEGACY_SOURCE_MAP[norm(lead.source)] || LEAD_ORIGIN.UNKNOWN;
};

export const isMarketplaceLead = (lead) => resolveLeadOrigin(lead) === LEAD_ORIGIN.MARKETPLACE;
export const isProviderOwnedLead = (lead) => resolveLeadOrigin(lead) === LEAD_ORIGIN.PROVIDER_OWNED;
